/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import type { DateRange } from 'react-aria';

import { DateRangePicker, localToday, DateRangePickerOnChange } from 'sarsaparilla';
import { type CalendarDate } from '@internationalized/date';

import { MAX_MONTHS_AHEAD } from 'ui-navigation/src/components/homepage/FlexDates/FlexDates';
import { useAppDispatch, useAppSelector } from '../redux/redux-helpers';

import { updateSelectedDate, updateSearchCriterias } from '../actions/search';
import { searchDate } from '../utils/dateUtils';

// This is a partial
type SearchFromRedux = {
    checkin_time: string; // weird format 12/19/2023
    checkout_time: string;
    sort: string;
} | null;

interface SearchCriteriaDatesProps {
    onApplyFilters?: () => void;
    onDatesChange: ({
        startDate,
        endDate,
    }: {
        startDate: CalendarDate | null;
        endDate: CalendarDate | null;
    }) => void;
}

/**
 * Renders a date range picker for selecting check-in and check-out dates.
 * The expected "source of truth" for the dates is the redux store, however this component
 * maintains its own local state to handle errors and gradual input changes. External changes
 * to the redux store will update the local state via a useEffect.
 *
 * This component only updates the redux store when the user has selected a valid date
 * range using the date picker.
 *
 * Do not attempt to update the redux store by any means other than the user selecting
 * a valid date range using the input, especially do not attempt some additional syncing
 * via a useEffect hook.
 */
export function SearchCriteriaDates({
    onApplyFilters,
    onDatesChange = () => {},
}: SearchCriteriaDatesProps) {
    const dispatch = useAppDispatch();
    const search = useAppSelector((state) => {
        return state?.search || null;
    }) as SearchFromRedux;

    // Creating some local state to store the start and end values ... this creates an intermediary
    // between the "real" state in the redux store and what the date picker displays.
    // This allows the date picker to behave correctly and be in an invalid state without
    // adding invalid values to the redux store, which would cause failures
    // in other parts of the app, such as fetching availability for the search results
    const [dateRangeValue, setDateRangeValue] = React.useState<DateRange | null>(() => {
        const start = search?.checkin_time ? searchDate(search?.checkin_time) : null;
        const end = search?.checkout_time ? searchDate(search?.checkout_time) : null;

        return start && end ? { start, end } : null;
    });

    const isInputInvalidRef = React.useRef(false);

    // If something external changes the dates in the redux store (such as a query param
    // change or a programmatic clearing of filters), update the local state to match
    React.useEffect(() => {
        // Bail on syncing if the input is in an invalid state ... user might still be typing
        if (isInputInvalidRef.current) return;

        const start = search?.checkin_time ? searchDate(search?.checkin_time) : null;
        const end = search?.checkout_time ? searchDate(search?.checkout_time) : null;

        const isSameStartDate = !start
            ? start === dateRangeValue
            : start.toString() === dateRangeValue?.start.toString();
        const isSameEndDate = !end
            ? end === dateRangeValue
            : end.toString() === dateRangeValue?.end.toString();

        // If the start and end dates are the same as the current dateRangeValue, do nothing
        if (isSameStartDate && isSameEndDate) {
            return;
        }

        if (start && end) {
            setDateRangeValue({ start, end });
        } else {
            setDateRangeValue(null);
        }
    }, [dateRangeValue, search?.checkin_time, search?.checkout_time]);

    const handleDateRangeChange: DateRangePickerOnChange = (value, validation) => {
        setDateRangeValue(value);

        // Bail if input is not valid to cover cases where the user is still typing
        if (validation && validation.isInvalid) {
            isInputInvalidRef.current = true;
            return;
        }

        isInputInvalidRef.current = false;
        let startString = '';
        let endString = '';

        if (value && value.start && value.end) {
            startString = searchDate(value?.start).format('MM/DD/YYYY');
            endString = searchDate(value?.end).format('MM/DD/YYYY');
        }

        dispatch(updateSelectedDate(startString, true));
        dispatch(updateSelectedDate(endString, false));

        if (onApplyFilters) {
            onApplyFilters();
        }

        if (onDatesChange) {
            onDatesChange({
                startDate: value?.start ? searchDate(value.start) : null,
                endDate: value?.end ? searchDate(value.end) : null,
            });
        }

        let sort = search?.sort;

        if (startString) {
            if (!sort || sort === '' || sort === 'score') {
                // default to available sort if dates are set
                sort = 'available';
            }
        } else if (sort === 'available') {
            // clear available sort if no dates are set
            sort = 'score';
        }

        dispatch(
            updateSearchCriterias({
                start: null,
                cursor_mark: null,
                sort,
            })
        );
    };

    return (
        <DateRangePicker
            label="Check In and Check Out Dates"
            isLabelVisible={false}
            value={dateRangeValue}
            onChange={handleDateRangeChange}
            minValue={localToday}
            maxValue={localToday.add({ months: MAX_MONTHS_AHEAD })}
            hasFloatingError={true}
        />
    );
}

// cSpell:ignore checkin_time, checkout_time, Criterias
